import React, {useState, useEffect} from 'react';
import propTypes from 'prop-types';
import {get} from 'lodash';
import styled from 'styled-components';
import {Grid, TextField} from '@material-ui/core';
import {Controller} from "react-hook-form";
import isPostalCode from 'validator/lib/isPostalCode';
import {theme} from '../../../../themes/default-theme';
import {phoneRegex} from '../../../../util/regex';
import Dropdown from '../../../../components/ui/Dropdown';
import getClosestDealersByPostcode from '../../../../util/getClosestDealersByPostcode';
import {getTopCoordinates} from '../../../../util/getClosestCoodinate';
import getSavedPostcode from '../../../../util/getSavedPostcode';


import {Loader} from 'google-maps';

const FormItem = styled.div`
  display: block;
  margin:${props => props.margin === 'none' ? `0` : `30px 0`};
  width: 100%;
  text-align: left;

  ${props => props.display === 0 && `
    display: none;
  `}
`

const FormCol = styled.div`

  ${theme.breakpoints.down('sm')} {
    ${props => props.position === 'left' && `
      margin-bottom: 30px;
    }`
  }

  ${theme.breakpoints.up('md')} {
    ${props => props.position === 'left' ? `
      padding-right: 15px;
    ` : `
      padding-left: 15px;
    `}
  }
`

const Error = styled.p`
  color: ${theme.palette.error.form};
  text-align: left;
`

const Step1 = ({formProps, countryCode, state, dealers, onFormChange, dealersSelect, setDealersSelect, setDefaultDealer, setDealerSummary, showDealersDropdown}) => {

  const [geocoder, setGeocoder] = useState(null);

  useEffect(() => {
    const asyncLoad = async () => {
      const loader = new Loader(process.env.GOOGLE_MAPS_API_KEY, {});
      const google = await loader.load();
      setGeocoder(new google.maps.Geocoder())
    }
    asyncLoad()
  }, [])

  const {
    control,
    errors
  } = formProps

  const textFieldProps = {
    as:TextField,
    control:control,
    rules:{required: true },
    variant:"outlined",
    fullWidth: true,
    defaultValue: ''
  }

  const [closestDealersState, setClosestDealers] = useState([])

  useEffect(() => {
    if (state.postcode && state.postcode.length === 4) {
      getDealerFromPostcode(state.postcode);
    }
  }, [state.postcode, geocoder])

  const getDealerFromPostcode = (postcode) => {

    const closestDealersBackup = getClosestDealersByPostcode(postcode, dealers, 3);

    const country = countryCode === 'AU' ? 'Australia' : 'New Zealand';
    var closestDealers;

    geocoder && geocoder.geocode( { 'address': 'postalcode+' + postcode + country}, (results, status) => {
      if (status == 'OK') {
        const latFunc = get(results, '[0].geometry.location.lat', null)
        const lngFunc = get(results, '[0].geometry.location.lng', null)

        if (latFunc && lngFunc) {
          const latLng = {
            latitude: latFunc(),
            longitude: lngFunc()
          }

          closestDealers = getTopCoordinates(latLng, dealers.map(item => {
            return {
              ...item,
              longitude: parseFloat(get(item, 'acf_dealers.longitude', '')),
              latitude: parseFloat(get(item, 'acf_dealers.latitude', '')),
            }
          }), 3);

        }
      } else {
        closestDealers = closestDealersBackup
      }

      const getDealer = () => {
        const dealerJson = localStorage.getItem('dealer');
        return dealerJson ? JSON.parse(dealerJson): null;
      }

      const lsDealer = getDealer();

      const savedDealer = lsDealer ? closestDealers.find(item => {
        return item.title.toLowerCase() === lsDealer.title.toLowerCase();
      }) : null

      const savedDealerFromList = lsDealer ? dealers.find(item => {
        return item.title.toLowerCase() === lsDealer.title.toLowerCase();
      }) : null

      const tempDealerArr = savedDealerFromList ? [savedDealerFromList] : []

      setClosestDealers([...tempDealerArr, ...closestDealers])

      setDealersSelect([...tempDealerArr, ...closestDealers].map(dealer => {
        return {
          id: dealer.acf_dealers.dealerID,
          value: dealer.acf_dealers.dealerID,
          text: dealer.title,
        }
      }))


      // Set default
      const closest = savedDealer ? savedDealer : (savedDealerFromList ? savedDealerFromList : closestDealers[0]);
      
      setDefaultDealer({
        id: closest.acf_dealers.dealerID,
        value: closest.acf_dealers.dealerID,
        text: closest.title,
      });
      setDealerSummary(closest)
    
    });

    
  }

  const handleDealerUpdate = (select) => {
    const seletedDealer = closestDealersState.find(item => {
      return item.title === select.text
    })
    setDealerSummary(seletedDealer);
    onFormChange();
  }
  


  return (
    <>
      {/* First name */}
      <FormItem>
        <Controller
          {...textFieldProps}
          name={'first_name'}
          placeholder={'First name*'}
          error={get(errors, `[first_name]`, false) ? true : false}
          defaultValue={state.first_name || ''}
        />
        {(get(errors, `[first_name]`, false)) && <Error>This field is required</Error>}
      </FormItem>
      {/* Last name */}
      <FormItem>
        <Controller
          {...textFieldProps}
          name={'last_name'}
          placeholder={'Last name*'}
          error={get(errors, `[last_name]`, false) ? true : false}
          defaultValue={state.last_name || ''}
        />
        {(get(errors, `[last_name]`, false)) && <Error>This field is required</Error>}

      </FormItem>
      {/* Email */}
      <FormItem>
        <Controller
          {...textFieldProps}
          type="email"
          name={'email'}
          placeholder={'Email*'}
          error={get(errors, `[email]`, false) ? true : false}
          defaultValue={state.email || ''}
        />
        {(get(errors, `[email]`, false)) && <Error>Please add a valid email address</Error>}

      </FormItem>
      {/* Contact number */}
      <FormItem>
        <Controller
          {...textFieldProps}
          type="tel"
          rules={{
            ...textFieldProps.rules,
            type: 'custom',
            minLength: 8,
            maxLength: 13
          }}
          name={'phone'}
          placeholder={'Contact number*'}
          error={get(errors, `[phone]`, false) ? true : false}
          defaultValue={state.phone || ''}
        />
        {(get(errors, `[phone]`, false)) && <Error>Please add a 8 digit phone number</Error>}

      </FormItem>

      <Grid container direction={'row'} justify={'space-between'} align={'center'}>
        <Grid item xs={12} md={6}>
          <FormCol position="left">
            <Controller
              {...textFieldProps}
              type="tel"
              rules={{
                ...textFieldProps.rules,
                type: 'custom',
                minLength: 3,
                maxLength: 4,
                validate: (val) => isPostalCode(val, 'AU')
              }}
              name={'postcode'}
              placeholder={'Postcode*'}
              error={get(errors, `[postcode]`, false) ? true : false}
              defaultValue={state.postcode || ''}
            />
            {(get(errors, `[postcode]`, false)) && <Error>Please add a valid 4 digit postcode</Error>}

          </FormCol>

        </Grid>
        <Grid item xs={12} md={6}>
          <FormCol position="right">
            <Controller
              {...textFieldProps}
              rules={{
                ...textFieldProps.rules,
                type: 'custom',
              }}
              name={'chassis'}
              placeholder={'Chassis number*'}
              error={get(errors, `[chassis]`, false) ? true : false}
              defaultValue={state.chassis || ''}
            />
            {(get(errors, `[chassis]`, false)) && <Error>This field is required</Error>}
          </FormCol>
        </Grid>

        {showDealersDropdown &&
          <FormItem display={dealersSelect.length > 0 ? 1 : 0}>
            <Controller
              as={
                <Dropdown 
                  variant="outlined"
                  showComplete={true}
                  error={get(errors, `[dealer]`, false) ? true : false}
                  label={'Service type*'}
                  options={dealersSelect}
                />
              }
              control={control}
              rules={{
                ...textFieldProps.rules,
                type: 'custom'
              }}
              defaultValue={typeof(state.dealer) === 'object' ? get(state, 'dealer.text', undefined) : state.dealer || undefined}
              name='dealer'
              onUpdate={handleDealerUpdate}
            />
            {/* <Controller
              as={
                <Dropdown 
                  variant="outlined"
                  showComplete={true}
                  error={get(errors, `[dealer]`, false) ? true : false}
                  label={'Select a dealer'}
                  options={dealersSelect}
                  formVal={state.dealer ? state.dealer : null}
                />
              }
              control={control}
              rules={{
                ...textFieldProps.rules,
                type: 'custom'
              }}
              name='dealer'
              onUpdate={onFormChange}
            /> */}
          </FormItem>
        }
      </Grid>

    </>
  )
}
  
Step1.propTypes = {
  setDealerSummary: propTypes.func,
  showDealersDropdown: propTypes.bool
}
  
Step1.defaultProps = {
  setDealerSummary: () => {},
  showDealersDropdown: true
}
  
export default Step1